import { useEffect, useRef, useState } from 'react'
import { alpha, type Theme } from '@mui/material/styles'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import {
  InputAdornment,
  TextField,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { ChevronLeft, ChevronRight, Clear, Search } from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { NAV_SECTIONS, type NavItem } from './navItems'

export const SIDEBAR_WIDTH_FULL = 240
export const SIDEBAR_WIDTH_ICONS = 64
export const SIDEBAR_WIDTH = SIDEBAR_WIDTH_FULL // For backwards compatibility

// Sidebar collapse modes: 0 = hidden, 1 = icons only, 2 = full
export type SidebarCollapseMode = 0 | 1 | 2

interface SidebarProps {
  mobileOpen?: boolean
  onDrawerToggle?: () => void
  isMobile?: boolean
  collapseMode?: SidebarCollapseMode
  onCollapseModeChange?: (mode: SidebarCollapseMode) => void
}

export function Sidebar({
  mobileOpen = false,
  onDrawerToggle,
  isMobile = false,
  collapseMode = 2,
  onCollapseModeChange,
}: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const [filter, setFilter] = useState('')
  const filterInputRef = useRef<HTMLInputElement>(null)
  const isAdmin = user?.roles?.includes('Admin')

  // The mobile drawer is always full width; only the desktop rail collapses.
  const isIconsOnly = !isMobile && collapseMode === 1
  const isHidden = collapseMode === 0
  const currentWidth = isIconsOnly ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  const handleCollapseToggle = () => {
    if (onCollapseModeChange) {
      // Cycle through modes: 2 -> 1 -> 0 -> 2
      const nextMode = collapseMode === 2 ? 1 : collapseMode === 1 ? 0 : 2
      onCollapseModeChange(nextMode as SidebarCollapseMode)
    }
  }

  // Role filter, applied per section so an empty section (all items filtered out) disappears
  // along with its heading.
  const visibleSections = isAuthenticated
    ? NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.requiresAdmin && !isAdmin) return false
          return true
        }),
      })).filter((section) => section.items.length > 0)
    : []

  // Quick filter: an item matches on its own label or its section's ("system" lists the whole
  // System group). Applied AFTER the role filter, so it can never reveal a gated page.
  const filterText = filter.trim().toLowerCase()
  const filteredSections = filterText
    ? visibleSections
        .map((section) => ({
          ...section,
          items: section.label?.toLowerCase().includes(filterText)
            ? section.items
            : section.items.filter((item) => item.label.toLowerCase().includes(filterText)),
        }))
        .filter((section) => section.items.length > 0)
    : visibleSections
  const firstMatch = filteredSections[0]?.items[0]

  const goTo = (path: string) => {
    setFilter('')
    navigate(path)
    if (isMobile) onDrawerToggle?.()
  }

  // Desktop shortcut: "/" or Ctrl/Cmd+K jumps to the filter. "/" is ignored while typing in a
  // field so it never steals a slash from a search box or editor. A collapsed rail is expanded
  // first, since the filter only exists in the full-width rail.
  useEffect(() => {
    if (isMobile || !isAuthenticated) return
    const onKeyDown = (e: KeyboardEvent) => {
      const isCtrlK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k'
      const target = e.target as HTMLElement | null
      const typing =
        !!target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      const isSlash = e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey
      if (!isCtrlK && !isSlash) return
      e.preventDefault()
      if (collapseMode !== 2) {
        onCollapseModeChange?.(2)
        // Let the rail render its full-width content before focusing
        setTimeout(() => filterInputRef.current?.focus(), 250)
      } else {
        filterInputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isMobile, isAuthenticated, collapseMode, onCollapseModeChange])

  const isItemActive = (item: NavItem) =>
    item.path === '/'
      ? location.pathname === '/'
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)

  const renderNavItem = (item: NavItem) => {
    const isActive = isItemActive(item)

    const buttonContent = (
      <ListItemButton
        component={RouterLink}
        to={item.path}
        selected={isActive}
        // Tapping a destination on a phone must dismiss the drawer covering the destination.
        onClick={isMobile ? onDrawerToggle : undefined}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          minHeight: 44,
          justifyContent: isIconsOnly ? 'center' : 'flex-start',
          px: isIconsOnly ? 1.5 : 2,
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
          },
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            '& .MuiListItemIcon-root': {
              color: 'primary.contrastText',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: isActive ? 'primary.contrastText' : 'primary.main',
            minWidth: isIconsOnly ? 'auto' : 40,
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!isIconsOnly && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: isActive ? 600 : 400,
            }}
          />
        )}
      </ListItemButton>
    )

    return (
      <ListItem key={item.path} disablePadding>
        {isIconsOnly ? (
          <Tooltip title={item.label} placement="right" arrow>
            {buttonContent}
          </Tooltip>
        ) : (
          buttonContent
        )}
      </ListItem>
    )
  }

  const mobileScrollRef = useRef<HTMLDivElement>(null)

  const drawerContent = (
    <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* The temporary drawer slides in UNDER the AppBar (which stays on top so the hamburger
          can close it). A Toolbar spacer is exactly the AppBar's height at every breakpoint and
          orientation (56 / 48 landscape / 64), where a hardcoded offset left a gap in landscape. */}
      {isMobile && <Toolbar sx={{ flexShrink: 0 }} />}

      {!isIconsOnly && visibleSections.length > 0 && (
        <Box sx={{ px: 1, pt: 1.5, pb: 0.5, flexShrink: 0 }}>
          <TextField
            inputRef={filterInputRef}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && firstMatch) {
                e.preventDefault()
                goTo(firstMatch.path)
              } else if (e.key === 'Escape') {
                setFilter('')
                filterInputRef.current?.blur()
              }
            }}
            placeholder="Jump to page"
            size="small"
            fullWidth
            inputProps={{ 'aria-label': 'Filter navigation' }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear filter" onClick={() => setFilter('')} edge="end">
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : !isMobile ? (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Ctrl K
                  </Typography>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
      )}

      <Box ref={isMobile ? mobileScrollRef : undefined} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <List sx={{ px: isIconsOnly ? 0.5 : 1, pt: isIconsOnly ? 1.5 : 1, pb: 2 }} disablePadding>
          {filterText && filteredSections.length === 0 && (
            <Typography component="li" variant="body2" color="text.secondary" sx={{ listStyle: 'none', px: 2, py: 1 }}>
              No page matches "{filter.trim()}"
            </Typography>
          )}
          {filteredSections.map((section, index) => (
            <Box key={section.key} component="li" sx={{ listStyle: 'none' }}>
              {/* In icons-only mode there is no room for a heading, so a rule separates the
                  groups instead. */}
              {section.label &&
                (isIconsOnly ? (
                  <Divider sx={{ my: 1 }} />
                ) : (
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      px: 2,
                      pt: index === 0 ? 0 : 1.5,
                      pb: 0.5,
                      color: 'text.secondary',
                      fontSize: '0.68rem',
                      letterSpacing: 1,
                      lineHeight: 1.6,
                    }}
                  >
                    {section.label}
                  </Typography>
                ))}
              <List disablePadding>{section.items.map(renderNavItem)}</List>
            </Box>
          ))}
        </List>
      </Box>
    </Box>
  )

  // Clear the filter once it has done its job (a click on a filtered item, Back, in-page links)
  useEffect(() => {
    setFilter('')
  }, [location.pathname])

  useEffect(() => {
    if (mobileOpen) mobileScrollRef.current?.scrollTo({ top: 0 })
    // A filter left over from the last opening would hide most of the menu
    else setFilter('')
  }, [mobileOpen])

  const drawerPaperStyles = {
    height: '100%',
    boxSizing: 'border-box',
    borderRight: '2px solid',
    borderColor: 'primary.main',
    // The wash and tint are background LAYERS, not pseudo-elements. They used to be two
    // absolutely-positioned ::before/::after overlays, and the paper is the scroll container — an
    // absolute child of a scroll container covers only its initial box, so on a phone, where the
    // list is taller than the screen, the wash ran out after one screen-height and everything
    // from "System" down sat on the raw pattern. A background paints the whole visible box
    // however far the list is scrolled. Top layer first. (The paper itself no longer scrolls:
    // an inner box does, so the filter box stays put above the list.)
    backgroundImage: (theme: Theme) =>
      `linear-gradient(${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.8)}), ` +
      `linear-gradient(${alpha(theme.palette.primary.main, 0.25)}, ${alpha(theme.palette.primary.main, 0.25)}), ` +
      `url('/assets/images/Navigation_Background.png')`,
    backgroundSize: 'auto, auto, 400px',
    backgroundRepeat: 'no-repeat, no-repeat, repeat',
    transition: 'width 0.2s ease-in-out',
    overflow: 'hidden',
  }

  return (
    <Box
      component="nav"
      sx={{
        width: { md: isHidden ? 0 : currentWidth },
        flexShrink: { md: 0 },
        transition: 'width 0.2s ease-in-out',
        position: 'relative',
        height: '100%',
      }}
    >
      {/* Collapse Toggle Button - positioned outside drawer */}
      {!isMobile && onCollapseModeChange && (
        <Tooltip
          title={collapseMode === 2 ? 'Collapse to icons' : collapseMode === 1 ? 'Hide sidebar' : 'Expand sidebar'}
          placement="right"
        >
          <IconButton
            onClick={handleCollapseToggle}
            size="small"
            sx={{
              display: { xs: 'none', md: 'flex' },
              position: 'absolute',
              top: 12,
              right: -12,
              zIndex: 1300,
              width: 24,
              height: 24,
              minWidth: 24,
              minHeight: 24,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              color: 'primary.main',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
              },
            }}
          >
            {collapseMode === 0 ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}

      {/* Mobile drawer - temporary, sits over the content and closes on navigation */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        // keepMounted preserves scroll position between openings, so the menu reopened wherever
        // it was last left — mid-list, with "Dashboard" out of sight. Back to the top each time
        // (the effect on mobileOpen above).
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            ...drawerPaperStyles,
            position: 'fixed',
            width: SIDEBAR_WIDTH_FULL,
            // Full height; the Toolbar spacer in drawerContent clears the AppBar.
            top: 0,
            height: '100%',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop rail - permanent, in flow next to the content */}
      {!isHidden && (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: currentWidth,
            height: '100%',
            flexShrink: 0,
            transition: 'width 0.2s ease-in-out',
            '& .MuiDrawer-paper': {
              ...drawerPaperStyles,
              position: 'relative',
              width: currentWidth,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  )
}
